import React, { useState, useEffect } from 'react';
import { User as UserType, UserCreate } from '../types';
import { usersAPI } from '../services/api';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  AppBar,
  Toolbar,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import RequestLimitsManagement from './RequestLimitsManagement';

const AdminSettings: React.FC = () => {
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'limits'>('users');
  const [newUser, setNewUser] = useState<UserCreate>({ email: '', role: 'regular', number_of_requests: 0 });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const userList = await usersAPI.getAll();
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAddingUser(true);
    setError(null);
    setSuccess(null);
    
    try {
      await usersAPI.create(newUser);
      setSuccess(`Utilizatorul ${newUser.email} a fost adăugat cu succes! Un email de bun venit a fost trimis.`);
      setNewUser({ email: '', role: 'regular', number_of_requests: 0 });
      await loadUsers();
      
      // Show success message for 2 seconds before closing dialog
      setTimeout(() => {
        setShowAddModal(false);
        setIsAddingUser(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to add user:', error);
      setError('Eroare la adăugarea utilizatorului. Te rugăm să încerci din nou.');
      setIsAddingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      setSuccess('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      setError('Failed to delete user');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AdminIcon sx={{ mr: 2, fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Admin Settings
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Breadcrumbs */}
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/dashboard');
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
          >
            <HomeIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Admin Settings
          </Typography>
        </Breadcrumbs>
      </Container>

      {/* Tab Navigation */}
      <Container maxWidth="lg" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={activeTab === 'users' ? 'contained' : 'text'}
              onClick={() => setActiveTab('users')}
              startIcon={<PersonIcon />}
              sx={{ textTransform: 'none' }}
            >
              Gestionarea Utilizatorilor
            </Button>
            <Button
              variant={activeTab === 'limits' ? 'contained' : 'text'}
              onClick={() => setActiveTab('limits')}
              startIcon={<AdminIcon />}
              sx={{ textTransform: 'none' }}
            >
              Limitele de Cereri
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ pb: 4, px: { xs: 1, sm: 2 } }}>
        {activeTab === 'users' ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1" sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                mb: { xs: 2, sm: 0 }
              }}>
                Gestionarea Utilizatorilor
              </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{ 
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 1.5, sm: 1.5 },
              mt: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Adaugă Utilizator Nou
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>User</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Role</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Requests</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        src={user.picture} 
                        alt={user.name}
                        sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}
                      >
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          display: { xs: 'block', sm: 'none' }
                        }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                      label={user.role}
                      color={user.role === 'admin' ? 'secondary' : 'primary'}
                      size="small"
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {user.number_of_requests}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        {formatDate(user.created_at)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete User">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteUser(user.id)}
                        size="small"
                      >
                        <DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add User Dialog */}
        <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleAddUser}>
            <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Adaugă Utilizator Nou</DialogTitle>
            <DialogContent>
              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                  size="small"
                />
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value as 'admin' | 'regular' })}
                    label="Rol"
                  >
                    <MenuItem value="regular">Utilizator Normal</MenuItem>
                    <MenuItem value="admin">Administrator</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Numărul de Cereri"
                  type="number"
                  value={newUser.number_of_requests}
                  onChange={(e) => setNewUser({ ...newUser, number_of_requests: parseInt(e.target.value) || 0 })}
                  required
                  size="small"
                  inputProps={{ min: 0 }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setShowAddModal(false)} 
                size="small"
                disabled={isAddingUser}
              >
                Anulează
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                size="small"
                disabled={isAddingUser}
                startIcon={isAddingUser ? <CircularProgress size={16} /> : null}
                color={success ? 'success' : 'primary'}
              >
                {isAddingUser ? (success ? 'Adăugat cu succes!' : 'Se adaugă...') : 'Adaugă Utilizator'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
          </>
        ) : (
          <RequestLimitsManagement />
        )}
      </Container>
    </Box>
  );
};

export default AdminSettings;
